/**
 * ACR Cloud 楽曲認識のユーティリティ
 */
import {
  DATE_DISPLAY_TYPE,
  formatDateText,
} from '../components/date-display/DateDisplay';

import type {
  AcrAiDetectionItem,
  AcrArtist,
  AcrMatchRow,
  AcrResultRow,
  AcrResultsResponse,
  AcrScanResult,
} from '../types/acr';

/** スコアは稀に 100 を超える値が返るため、表示時は 100 にキャップする。 */
export const capAcrScore = (score: number): number => Math.min(score, 100);

/** 行が照合中（pending / processing）かどうか。UI 上はどちらも「楽曲照合中」。 */
export const isAcrRowInProgress = (row: AcrResultRow): boolean =>
  row.acr_scan_status === 'pending' || row.acr_scan_status === 'processing';

/**
 * ACR 結果一覧レスポンス内の照合中（pending / processing）行数を数える。
 * 一覧ページのポーリング継続判定と「照合中 N 件」表示に使う。
 */
export function countInProgressAcrRows(data?: AcrResultsResponse): number {
  if (!data) return 0;
  return (
    data.data.shot_media.filter(isAcrRowInProgress).length +
    data.data.shot_comment_media.filter(isAcrRowInProgress).length +
    data.data.talk_room_media.filter(isAcrRowInProgress).length
  );
}

/** アーティスト配列を ", " 区切りの文字列に整形する。 */
export const formatAcrArtists = (artists?: AcrArtist[]): string =>
  artists?.map((a) => a.name).join(', ') || '';

/** 秒数を "H:MM:SS" / "MM:SS" 形式に整形する（MediaDetailModal と同じ挙動）。 */
export const formatAcrTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/** DB 上の開始位置(ms)と再生時間(秒)から一致区間 "start - end" を整形する。 */
export const formatAcrTimeRange = (
  dbBeginMs: number,
  playedDuration: number,
): string => {
  const startSec = Math.round(dbBeginMs / 1000);
  const endSec = startSec + (playedDuration ?? 0);
  return `${formatAcrTime(startSec)} - ${formatAcrTime(endSec)}`;
};

/**
 * AI 生成判定（`ai_detection`）をファイル単位の 1 行サマリに整形する。
 * 最大確率の区間を代表として `AI {pct}% / {likely_source}` を返す。
 * 判定が無い場合は空文字。
 */
export function formatAiSummary(aiDetection?: AcrAiDetectionItem[]): string {
  if (!aiDetection || aiDetection.length === 0) return '';
  const top = [...aiDetection].sort(
    (a, b) => b.ai_probability - a.ai_probability,
  )[0];
  if (!top) return '';
  const pct = Number.isFinite(top.ai_probability)
    ? top.ai_probability.toFixed(0)
    : '';
  const source = top.likely_source || top.prediction || '';
  if (!pct) return source;
  return source ? `AI ${pct}% / ${source}` : `AI ${pct}%`;
}

/**
 * 照合あり/なしアイコン（プレビュー流用）の表示可否と色を返す。
 * - matched: 青（楽曲照合あり）
 * - pending / processing: グレー（照合中）
 * - no_match / error / その他: 非表示
 * 判定ロジックは AudioPreview / VideoPreview の `hasAcrIcon` / `acrIconColor` と同一。
 */
export function getAcrIconMeta(
  status?: string,
): { show: boolean; color: string } {
  switch (status) {
    case 'matched':
      return { show: true, color: 'var(--soremo-blue)' };
    case 'pending':
    case 'processing':
      return { show: true, color: 'var(--soremo-border)' };
    default:
      return { show: false, color: '' };
  }
}

/**
 * `acr_scan_result` を「1 照合結果＝1 行」の配列へ平坦化する。
 * 原盤(music) → 複製(cover_songs) → カスタム(custom_files) の順。
 * 照合が 1 件も無い場合は空配列を返す（呼び出し側で空行を 1 行描画する想定）。
 */
export function flattenAcrRowToMatchRows(
  result: AcrScanResult | null | undefined,
): AcrMatchRow[] {
  if (!result) return [];
  const rows: AcrMatchRow[] = [];

  (result.music ?? []).forEach((item) => {
    const r = item.result;
    rows.push({
      category: '原盤',
      title: r?.title ?? '',
      artists: formatAcrArtists(r?.artists),
      score: r?.score != null ? `${capAcrScore(r.score)}%` : '',
      isrc: r?.external_ids?.isrc ?? '',
      timeRange:
        r?.db_begin_time_offset_ms != null
          ? formatAcrTimeRange(r.db_begin_time_offset_ms, item.played_duration)
          : '',
      duration:
        item.played_duration != null ? formatAcrTime(item.played_duration) : '',
    });
  });

  (result.cover_songs ?? []).forEach((item) => {
    const r = item.result;
    rows.push({
      category: '複製（カバー）',
      title: r?.title ?? '',
      artists: formatAcrArtists(r?.artists),
      score: '',
      isrc: r?.external_ids?.isrc ?? '',
      timeRange: '',
      duration: '',
    });
  });

  (result.custom_files ?? []).forEach((item) => {
    const r = item.result;
    rows.push({
      category: 'カスタム',
      title: r?.title ?? '',
      artists: '',
      score: r?.score != null ? `${capAcrScore(r.score)}%` : '',
      isrc: '',
      timeRange:
        r?.db_begin_time_offset_ms != null
          ? formatAcrTimeRange(r.db_begin_time_offset_ms, item.played_duration)
          : '',
      duration:
        item.played_duration != null ? formatAcrTime(item.played_duration) : '',
    });
  });

  return rows;
}

/**
 * ACRスキャンステータスに対応するメニュー表示用ラベルと色を返す。
 * ACR非対応（undefined）の場合は null を返す。
 */
export function getAcrStatusLabel(
  status?: string
): { label: string; color: string } | null {
  switch (status) {
    case 'matched':
      return { label: '楽曲照合あり', color: 'var(--soremo-blue)' };
    case 'pending':
    case 'processing':
      return { label: '楽曲照合中', color: 'var(--soremo-light-gray)' };
    case 'no_match':
    case 'error':
      return { label: '楽曲照合なし', color: 'var(--soremo-light-gray)' };
    default:
      return null;
  }
}

/**
 * 「詳細情報」ラベルにACRステータスを括弧付きで合成して返す。
 * ACR非対応の場合は「詳細情報」のみ。
 */
export function getDetailLabelWithAcr(
  acrScanStatus?: string
): { label: string; color?: string } {
  const acrLabel = getAcrStatusLabel(acrScanStatus);
  if (!acrLabel) {
    return { label: '詳細情報' };
  }
  return {
    label: `詳細情報（${acrLabel.label}）`,
    color: acrScanStatus === 'matched' ? acrLabel.color : undefined,
  };
}

/**
 * アップロード日時を soremo 共通の日時表記（`M/D H:mm`、他年は年付き）に整形する。
 * 無効・空は空文字。表示・TSV 出力の両方でこれを使い、表記を揃える。
 */
export const formatAcrUploadedAt = (iso: string | null | undefined): string =>
  formatDateText(iso, DATE_DISPLAY_TYPE.DATE_TIME);

const ACR_TSV_KIND_LABELS: Record<
  'shot' | 'shot_comment' | 'talk_room',
  string
> = {
  shot: 'ショット内メディア',
  shot_comment: 'ショットコメント',
  talk_room: 'トークルーム',
};

const ACR_TSV_HEADER = [
  '区分',
  'ショット名',
  'アップロード日時',
  'ファイル名',
  'ACRステータス',
  'カテゴリ',
  'タイトル',
  'アーティスト',
  'スコア',
  'ISRC',
  '一致区間',
  '一致時間',
  'AI判定',
];

/**
 * TSV セル用サニタイズ。Excel / Google Sheets はタブ区切りテキストを自動的に
 * セル分割するため、タブ区切りで出力する。セル内のタブ・改行はスペースに
 * 置換して列ズレを防ぐ。
 */
const sanitizeTsvCell = (raw: string | number | null | undefined): string => {
  const value = raw === null || raw === undefined ? '' : String(raw);
  return value.replace(/[\t\n\r]+/g, ' ').trim();
};

/**
 * ACR 結果 3 区分をひとつの TSV にまとめる（クリップボード貼り付け用）。
 * 1 照合結果＝1 行。照合が無いファイルは照合列が空の 1 行で出力する。
 */
export function buildAcrTsv(
  shotMedia: AcrResultRow[],
  shotCommentMedia: AcrResultRow[],
  talkRoomMedia: AcrResultRow[],
): string {
  const toLine = (
    kind: keyof typeof ACR_TSV_KIND_LABELS,
    row: AcrResultRow,
    match: AcrMatchRow | null,
  ): string =>
    [
      ACR_TSV_KIND_LABELS[kind],
      row.shot_name ?? '',
      formatAcrUploadedAt(row.uploaded_at),
      row.file_name,
      getAcrStatusLabel(row.acr_scan_status)?.label ?? row.acr_scan_status,
      match?.category ?? '',
      match?.title ?? '',
      match?.artists ?? '',
      match?.score ?? '',
      match?.isrc ?? '',
      match?.timeRange ?? '',
      match?.duration ?? '',
      formatAiSummary(row.acr_scan_result?.ai_detection),
    ]
      .map(sanitizeTsvCell)
      .join('\t');

  const rowsToLines = (
    kind: keyof typeof ACR_TSV_KIND_LABELS,
    rows: AcrResultRow[],
  ) =>
    rows.flatMap((row) => {
      const matches = flattenAcrRowToMatchRows(row.acr_scan_result);
      if (matches.length === 0) return [toLine(kind, row, null)];
      return matches.map((match) => toLine(kind, row, match));
    });

  return [
    ACR_TSV_HEADER.map(sanitizeTsvCell).join('\t'),
    ...rowsToLines('shot', shotMedia),
    ...rowsToLines('shot_comment', shotCommentMedia),
    ...rowsToLines('talk_room', talkRoomMedia),
  ].join('\n');
}
